/**
 * Domain pack sxemasi. Pack — kod emas, ma'lumot:
 *   packs/<id>/pack.json          — manifest (id, version, title)
 *   packs/<id>/components.json    — moddalar (yoki components/*.json)
 *   packs/<id>/labs/*.json        — laboratoriya ishlari
 *   packs/<id>/flowsheets/*.json  — texnologik sxemalar
 *   packs/<id>/i18n/<locale>.json — pack matnlari (modda nomlari va h.k.)
 * Engine pack'ni faqat `assemblePack` orqali qabul qiladi; fayl tizimi/bundler
 * bilan ishlash CLI (Node) va frontend (Vite glob) zimmasida.
 */
import { z } from 'zod';
import { I18nText } from '../scenario/schema';

const range = z.tuple([z.number(), z.number()]);
const propBase = { source: z.string(), uncertainty: z.string().optional(), range };
const Poly = z.object({ ...propBase, form: z.literal('poly'), variable: z.enum(['K', 'C']), coeffs: z.array(z.number()).min(1), units: z.string() }).strict();
const Shomate = z.object({ ...propBase, form: z.literal('shomate'), A: z.number(), B: z.number(), C: z.number(), D: z.number(), E: z.number() }).strict();
const Antoine = z.object({ ...propBase, form: z.literal('log10-mmHg-C'), A: z.number(), B: z.number(), C: z.number() }).strict();
const Vogel = z.object({ ...propBase, form: z.literal('vogel'), A: z.number(), B: z.number(), C: z.number() }).strict();
const Sutherland = z.object({ ...propBase, form: z.literal('sutherland'), mu0: z.number(), T0: z.number(), S: z.number() }).strict();
const Hvap = z.object({ atTb: z.number().positive(), method: z.literal('watson'), n: z.number(), source: z.string() }).strict();

export const SubstanceSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    nameKey: z.string(),
    formula: z.string(),
    molarMass: z.number().positive(),
    Tb: z.number().nullable(),
    Tc: z.number().nullable(),
    Pc: z.number().nullable(),
    antoine: Antoine.nullable(),
    cpLiquid: Poly.nullable(),
    cpGas: z.union([Poly, Shomate]).nullable(),
    hvap: Hvap.nullable(),
    densityLiquid: Poly.nullable(),
    viscosityLiquid: Vogel.nullable(),
    viscosityGas: Sutherland.nullable(),
    conductivityLiquid: Poly.nullable(),
    conductivityGas: Poly.nullable(),
  })
  .strict();

export const PackManifest = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    version: z.number().int().min(1),
    title: I18nText,
    description: I18nText.optional(),
    /** Yo'nalish mavzulari (bosh sahifa uchun) */
    topics: z.array(I18nText).optional(),
    /** Qaysi kurs/fan uchun */
    course: I18nText.optional(),
    /** Shu pack'lardan keyin yuklanadi; ularning moddalari/reaksiyalariga tayanishi mumkin */
    dependencies: z.array(z.string()).default([]),
  })
  .strict();
export type PackManifest = z.infer<typeof PackManifest>;

/** Pack'ning xom (fayllardan yig'ilgan) ko'rinishi */
export interface RawPack {
  manifest: unknown;
  components: unknown[];
  labs: unknown[];
  reactions: unknown[];
  flowsheets: unknown[];
  i18n: Record<string, Record<string, string>>;
}
